import { NICKNAME_MAX, NICKNAME_MIN, PASSWORD_MIN } from '@shefira/shared';

import { login, requestPasswordReset, resetPassword } from '../net/account.js';
import { AuthError, registerGuest, type StoredSession } from '../net/session.js';
import { leaveForProvider, PROVIDER_NAMES, signInProviders } from '../net/sign-in.js';
import { isNativeApp } from '../platform/native.js';
import { CONTACT_EMAIL, INSTAGRAM_URL } from './contact.js';
import { DISCORD_INVITE } from './discord.js';
import { legalUrl } from './legal-links.js';
import { providerButton } from './provider-button.js';
import { isTouch } from './touch.js';
import { showCustomise } from './customise.js';
import { versionLine } from './changelog-modal.js';
import { uiFormat, uiHtml, uiText } from './text.js';
import './kit.css';
import './front.css';
import type { AccountSession } from '@shefira/shared';

/**
 * The first screen: play at once as a guest, sign in to an existing
 * account, or come in with Google or Steam (ADR 0054). A guest is still the fast path (CADRAGE, "moins de 5 secondes");
 * the account form is one click away for players who already have one.
 *
 * Plain DOM rather than a Phaser scene: text inputs, focus, password
 * managers and mobile keyboards all come for free.
 */
export type WelcomeResult =
  { kind: 'character'; session: StoredSession } | { kind: 'account'; session: AccountSession };

export interface WelcomeOptions {
  /** A sign-in that came back refused (ADR 0054). */
  signInError?: string;
  /** The page was opened from a mailed reset link (ADR 0056): its face shows first. */
  resetToken?: string;
}

type Face = 'guest' | 'account' | 'forgot' | 'reset';
const FACES: readonly Face[] = ['guest', 'account', 'forgot', 'reset'];

export function showWelcome({
  signInError,
  resetToken,
}: WelcomeOptions = {}): Promise<WelcomeResult> {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'login';
    overlay.innerHTML = uiHtml(`
      <div class="login__stack">
        <h1 class="login__title login__brand"><img class="login__logo" src="/assets/ui/logo.png" alt="Shefira" width="168" height="68" /><img class="login__beta" src="/assets/ui/beta.png" alt="Bêta" width="73" height="29" /></h1>
        <p class="login__pitch">Un MMORPG en pixel art inspiré de BrowserQuest, dans ton navigateur : gratuit, sans inscription, en jeu en cinq secondes.</p>
        <ul class="welcome__perks" aria-label="Le jeu">
          <li class="kit-chip"><span class="kit-icon" style="background-image: url('/assets/ui/icon-map.png')" aria-hidden="true"></span>Un monde ouvert</li>
          <li class="kit-chip"><span class="kit-icon" style="background-image: url('/assets/ui/icon-stat-bosses.png')" aria-hidden="true"></span>Boss et donjons</li>
          <li class="kit-chip"><span class="kit-icon" style="background-image: url('/assets/ui/icon-guild.png')" aria-hidden="true"></span>Groupes et guildes</li>
        </ul>
        <div class="login__panel login__panel--welcome">
          <form class="login__form" data-form="guest" novalidate>
            <label class="welcome__label" for="welcome-nickname">Choisis ton pseudo</label>
            <input class="login__input" id="welcome-nickname" name="nickname" autocomplete="off" autocapitalize="off"
                   spellcheck="false" minlength="${String(NICKNAME_MIN)}" maxlength="${String(NICKNAME_MAX)}"
                   placeholder="Pseudo" />
            <button class="login__submit login__submit--gold" type="submit">Entrer dans le monde</button>
            <p class="login__hint welcome__small">Sans inscription : tu pourras créer un compte plus tard, depuis le jeu.</p>
            <button class="login__link welcome__switch" type="button" data-show="account">J’ai déjà un compte</button>
          </form>

          <form class="login__form" data-form="account" hidden novalidate>
            <p class="welcome__label">Retrouve tes personnages</p>
            <input class="login__input" name="email" type="email" autocomplete="email"
                   placeholder="E-mail" aria-label="E-mail" />
            <input class="login__input" name="password" type="password" autocomplete="current-password"
                   minlength="${String(PASSWORD_MIN)}" placeholder="Mot de passe" aria-label="Mot de passe" />
            <button class="login__submit login__submit--gold" type="submit">Se connecter</button>
            <button class="login__link welcome__forgot" type="button" data-show="forgot">Mot de passe oublié ?</button>
            <button class="login__link welcome__switch" type="button" data-show="guest">Jouer sans compte</button>
          </form>

          <form class="login__form" data-form="forgot" hidden novalidate>
            <p class="welcome__label">Mot de passe oublié</p>
            <p class="login__hint welcome__small">Donne l’adresse de ton compte : tu y recevras un lien pour choisir un nouveau mot de passe.</p>
            <input class="login__input" name="email" type="email" autocomplete="email"
                   placeholder="E-mail" aria-label="E-mail" />
            <button class="login__submit login__submit--gold" type="submit">Recevoir le lien</button>
            <button class="login__link welcome__switch" type="button" data-show="account">Retour à la connexion</button>
          </form>

          <form class="login__form" data-form="reset" hidden novalidate>
            <p class="welcome__label">Nouveau mot de passe</p>
            <input class="login__input" name="password" type="password" autocomplete="new-password"
                   minlength="${String(PASSWORD_MIN)}" placeholder="Nouveau mot de passe" aria-label="Nouveau mot de passe" />
            <input class="login__input" name="confirm" type="password" autocomplete="new-password"
                   minlength="${String(PASSWORD_MIN)}" placeholder="Encore une fois" aria-label="Confirme le mot de passe" />
            <button class="login__submit login__submit--gold" type="submit">Changer et se connecter</button>
            <button class="login__link welcome__switch" type="button" data-show="forgot">Demander un nouveau lien</button>
          </form>

          <div class="welcome__providers" hidden>
            <p class="login__hint welcome__or">ou</p>
          </div>

          <p class="login__hint welcome__notice" role="status" hidden></p>
          <p class="login__error" role="alert" hidden></p>
        </div>
        <div class="welcome__community">
        <a class="welcome__discord" href="${DISCORD_INVITE}" target="_blank" rel="noopener">
          <span class="welcome__discord-icon" aria-hidden="true"></span>
          <span class="welcome__discord-text">
            <strong class="welcome__discord-title">Rejoins la communauté sur Discord</strong>
            <span class="welcome__discord-sub">Annonces, groupes, bugs et suggestions</span>
          </span>
        </a>
        <a class="welcome__social" href="${INSTAGRAM_URL}" target="_blank" rel="noopener" aria-label="Instagram" title="Instagram">
          <span class="welcome__social-icon" aria-hidden="true"></span><span class="welcome__social-label">Suis-nous sur Instagram</span>
        </a>
        </div>
        <p class="login__footer">
          <a class="login__footer-link" href="${legalUrl('terms-of-use')}" target="_blank" rel="noopener">CGU</a>
          <span class="login__footer-sep" aria-hidden="true">·</span>
          <a class="login__footer-link" href="${legalUrl('terms-of-sale')}" target="_blank" rel="noopener">CGV</a>
          <span class="login__footer-sep" aria-hidden="true">·</span>
          <a class="login__footer-link" href="${legalUrl('privacy')}" target="_blank" rel="noopener">Données personnelles</a>
          <span class="login__footer-sep" aria-hidden="true">·</span>
          <a class="login__footer-link" href="${legalUrl('legal-notice')}" target="_blank" rel="noopener">Mentions légales</a>
          <span class="login__footer-sep" aria-hidden="true">·</span>
          <a class="login__footer-link" href="mailto:${CONTACT_EMAIL}?subject=Shefira%20%E2%80%94%20probl%C3%A8me">Signaler un problème</a>
        </p>
      </div>
    `);
    // No browser in the iOS and Android apps (ADR 0075).
    const pitch = overlay.querySelector('.login__pitch');
    if (pitch !== null && isNativeApp()) {
      pitch.textContent = uiText(
        'Un MMORPG en pixel art inspiré de BrowserQuest : gratuit, sans inscription, en jeu en cinq secondes.',
      );
    }
    overlay.querySelector('.login__brand')?.after(versionLine({ discord: false }));
    document.body.append(overlay);

    const find = (selector: string): HTMLElement => {
      const found = overlay.querySelector<HTMLElement>(selector);
      if (found === null) throw new Error(`Welcome markup is missing ${selector}`);
      return found;
    };
    const error = find('.login__error');
    const notice = find('.welcome__notice');
    const forms: Record<Face, HTMLFormElement> = {
      guest: find('[data-form="guest"]') as HTMLFormElement,
      account: find('[data-form="account"]') as HTMLFormElement,
      forgot: find('[data-form="forgot"]') as HTMLFormElement,
      reset: find('[data-form="reset"]') as HTMLFormElement,
    };
    const providers = find('.welcome__providers');
    let offersProviders = false;

    // Playing is the fast path; the account form is the other face of the
    // panel, one click away and one click back; the forgotten password hangs off it.
    const show = (wanted: Face, focus = true): void => {
      for (const face of FACES) forms[face].hidden = face !== wanted;
      providers.hidden = !offersProviders || wanted === 'forgot' || wanted === 'reset';
      error.hidden = true;
      notice.hidden = true;
      if (focus) forms[wanted].querySelector('input')?.focus();
    };
    for (const toggle of overlay.querySelectorAll<HTMLButtonElement>('[data-show]')) {
      toggle.addEventListener('click', () => {
        const wanted = FACES.find((face) => face === toggle.dataset.show) ?? 'guest';
        // Carry the address typed in one form over to the other.
        const typed = forms.account.querySelector<HTMLInputElement>('input[name="email"]');
        const forgot = forms.forgot.querySelector<HTMLInputElement>('input[name="email"]');
        if (wanted === 'forgot' && typed !== null && forgot !== null && forgot.value === '') {
          forgot.value = typed.value;
        }
        show(wanted);
      });
    }
    // On a touch screen a focused field opens the keyboard over half the
    // screen before the player has even seen the page: they tap it themselves.
    show(resetToken !== undefined ? 'reset' : 'guest', !isTouch());

    // Google and Steam (ADR 0054), under both faces of the panel, once the server says which it offers.
    void signInProviders().then((offered) => {
      for (const provider of offered) {
        const button = providerButton(
          provider,
          uiFormat('Continuer avec {provider}', { provider: PROVIDER_NAMES[provider] }),
          `login__submit welcome__provider welcome__provider--${provider}`,
        );
        button.addEventListener('click', () => {
          error.hidden = true;
          button.disabled = true;
          leaveForProvider(provider).catch((cause: unknown) => {
            button.disabled = false;
            error.textContent = uiText(
              cause instanceof AuthError ? cause.message : 'Connexion impossible',
            );
            error.hidden = false;
          });
        });
        providers.append(button);
      }
      offersProviders = offered.length > 0;
      providers.hidden = !offersProviders || !forms.forgot.hidden || !forms.reset.hidden;
    });

    const submitting = (form: HTMLFormElement, busy: boolean): void => {
      const button = form.querySelector<HTMLButtonElement>('button[type="submit"]');
      if (button !== null) button.disabled = busy;
    };
    if (signInError !== undefined) {
      error.textContent = uiText(signInError);
      error.hidden = false;
    }
    const fail = (form: HTMLFormElement, cause: unknown): void => {
      error.textContent = uiText(
        cause instanceof AuthError ? cause.message : 'Connexion impossible',
      );
      error.hidden = false;
      submitting(form, false);
    };

    forms.guest.addEventListener('submit', (event) => {
      event.preventDefault();
      error.hidden = true;
      submitting(forms.guest, true);
      const nickname = new FormData(forms.guest).get('nickname');
      const nicknameStr = typeof nickname === 'string' ? nickname.trim() : '';

      (async () => {
        try {
          overlay.hidden = true;
          const choice = await showCustomise(nicknameStr || 'Ton personnage');
          if (choice === null) {
            overlay.hidden = false;
            submitting(forms.guest, false);
            return;
          }
          const session = await registerGuest(nicknameStr, choice.classId, choice.look);
          overlay.remove();
          resolve({ kind: 'character', session });
        } catch (cause: unknown) {
          overlay.hidden = false;
          fail(forms.guest, cause);
        }
      })().catch(() => {
        /* error already displayed */
      });
    });

    forms.account.addEventListener('submit', (event) => {
      event.preventDefault();
      error.hidden = true;
      submitting(forms.account, true);
      const data = new FormData(forms.account);
      const email = data.get('email');
      const password = data.get('password');
      login(typeof email === 'string' ? email : '', typeof password === 'string' ? password : '')
        .then((session) => {
          overlay.remove();
          resolve({ kind: 'account', session });
        })
        .catch((cause: unknown) => {
          fail(forms.account, cause);
        });
    });

    forms.forgot.addEventListener('submit', (event) => {
      event.preventDefault();
      error.hidden = true;
      notice.hidden = true;
      const email = new FormData(forms.forgot).get('email');
      const address = typeof email === 'string' ? email.trim() : '';
      if (address === '') {
        fail(forms.forgot, new AuthError('Donne ton adresse e-mail', 'invalid_request'));
        return;
      }
      submitting(forms.forgot, true);
      requestPasswordReset(address)
        .then(() => {
          // Never says whether the address is known: that would tell anyone who plays here.
          notice.textContent = uiFormat(
            'Si un compte utilise {address}, un lien vient d’y partir. Pense à regarder dans les indésirables.',
            { address },
          );
          notice.hidden = false;
          submitting(forms.forgot, false);
        })
        .catch((cause: unknown) => {
          fail(forms.forgot, cause);
        });
    });

    forms.reset.addEventListener('submit', (event) => {
      event.preventDefault();
      error.hidden = true;
      const data = new FormData(forms.reset);
      const password = data.get('password');
      const confirm = data.get('confirm');
      if (resetToken === undefined) {
        fail(
          forms.reset,
          new AuthError('Ce lien n’est plus valable : demande-en un nouveau.', 'reset_invalid'),
        );
        return;
      }
      if (typeof password !== 'string' || password.length < PASSWORD_MIN) {
        fail(
          forms.reset,
          new AuthError(
            uiFormat('Le mot de passe fait au moins {count} caractères', { count: PASSWORD_MIN }),
            'invalid_request',
          ),
        );
        return;
      }
      if (password !== confirm) {
        fail(
          forms.reset,
          new AuthError('Les deux mots de passe ne sont pas les mêmes', 'invalid_request'),
        );
        return;
      }
      submitting(forms.reset, true);
      resetPassword(resetToken, password)
        .then((session) => {
          overlay.remove();
          resolve({ kind: 'account', session });
        })
        .catch((cause: unknown) => {
          fail(forms.reset, cause);
        });
    });
  });
}
